import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Loader2, CheckCircle2, XCircle, AlertTriangle, MinusCircle, Rocket, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function j(path, method = "GET", body) {
  const token = localStorage.getItem("arca_token");
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) { let d = "Request failed"; try { d = (await res.json()).detail || d; } catch { /* noop */ } throw new Error(d); }
  return res.json();
}

const ICON = {
  ok: { I: CheckCircle2, c: "text-emerald-400" },
  warning: { I: AlertTriangle, c: "text-amber-400" },
  failed: { I: XCircle, c: "text-red-400" },
  skipped: { I: MinusCircle, c: "text-slate-500" },
};
const STEP_LABEL = {
  readiness: "Global readiness check", review: "AI security & code review", auto_fix: "Safe auto-fixes",
  deploy: "Deploy to CDN provider", monitoring: "Enable monitoring", status_page: "Public status page",
};

export default function PublishWorldwide({ projectId }) {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    j("/deploy/providers").then((d) => setProviders((d.providers || []).filter((p) => p.deployable && p.connected))).catch(() => {});
  }, []);

  const publish = async () => {
    setBusy(true); setResult(null);
    try {
      const { job_id } = await j(`/architect/projects/${projectId}/publish-worldwide`, "POST", { provider: provider || null, auto_fix: true });
      // Poll the async job so long LLM work never hits the request timeout.
      const started = Date.now();
      const poll = async () => {
        const job = await j(`/publish-jobs/${job_id}`);
        setResult(job);
        if (job.status === "running" && Date.now() - started < 180000) {
          setTimeout(poll, 2500);
        } else {
          setBusy(false);
          job.success ? toast.success("Global publish pipeline complete") : toast.error(job.status === "failed" ? "Publish failed" : "Publish finished with issues");
        }
      };
      poll();
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <div className="mt-8 rounded-2xl border border-[#0055FF]/30 bg-gradient-to-br from-[#0055FF]/10 to-transparent p-5" data-testid="publish-worldwide-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-display font-bold text-lg flex items-center gap-2"><Globe size={18} className="text-[#00F0FF]" /> Publish Worldwide</p>
          <p className="text-slate-400 text-sm mt-1">One click runs readiness, AI review, safe auto-fixes, deploy, monitoring and a public status page.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={provider} onChange={(e) => setProvider(e.target.value)} className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-300 outline-none focus:border-[#0055FF]" data-testid="publish-provider-select">
            <option value="">No deploy (checks only)</option>
            {providers.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
          <button onClick={publish} disabled={busy} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-2.5 rounded-xl font-semibold text-sm shadow-[0_0_30px_rgba(0,85,255,0.5)] disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="publish-worldwide-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} Publish Worldwide
          </button>
        </div>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-5 space-y-2 overflow-hidden" data-testid="publish-result">
            {result.steps.map((s, i) => {
              const m = ICON[s.status] || ICON.skipped; const I = m.I;
              return (
                <div key={i} className="flex items-start gap-3 rounded-xl bg-black/30 px-3.5 py-2.5" data-testid={`publish-step-${s.name}`}>
                  <I size={16} className={`${m.c} mt-0.5 shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{STEP_LABEL[s.name] || s.name}</p>
                    <p className="text-slate-400 text-xs">{s.detail}</p>
                  </div>
                </div>
              );
            })}
            {result.status_slug && (
              <Link to={`/status/${result.status_slug}`} target="_blank" className="inline-flex items-center gap-1.5 text-[#00F0FF] text-sm hover:text-white mt-1" data-testid="publish-status-link">
                <ExternalLink size={14} /> View public status page
              </Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
